import React from 'react';

interface NewCustomerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerType: 'customer' | 'prospect' | 'suspect';
  onSubmit: (e: React.FormEvent) => void;
  isCreating: boolean;
  form: {
    name: string;
    email: string;
    phone: string;
    company: string;
    status: 'active' | 'inactive';
    lineType: 'personal' | 'commercial';
    secondaryContact: string;
  };
  setForm: React.Dispatch<React.SetStateAction<{
    name: string;
    email: string;
    phone: string;
    company: string;
    status: 'active' | 'inactive';
    lineType: 'personal' | 'commercial';
    secondaryContact: string;
  }>>;
  includeAddress: boolean;
  setIncludeAddress: (include: boolean) => void;
  address: {
    street1: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    addressType: string;
    label: string;
  };
  setAddress: React.Dispatch<React.SetStateAction<{
    street1: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    addressType: string;
    label: string;
  }>>;
}

const NewCustomerModal: React.FC<NewCustomerModalProps> = ({
  isOpen,
  onClose,
  customerType,
  onSubmit,
  isCreating,
  form,
  setForm,
  includeAddress,
  setIncludeAddress,
  address,
  setAddress,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-xl shadow-xl max-w-lg w-full mx-auto my-8 p-6 max-h-full overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-secondary dark:text-dark-text flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-primary">
              <path d="M15,14C12.33,14 7,15.33 7,18V20H23V18C23,15.33 17.67,14 15,14M1,10V12H9V10M15,12A4,4 0 0,0 19,8A4,4 0 0,0 15,4A4,4 0 0,0 11,8A4,4 0 0,0 15,12Z" />
            </svg>
            Add {customerType === 'customer' ? 'Customer' :
                 customerType === 'prospect' ? 'Prospect' :
                 'Suspect'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Line Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
              Line Type
            </label>
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, lineType: 'commercial' }))}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                  form.lineType === 'commercial'
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-700 text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Commercial
              </button>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, lineType: 'personal' }))}
                className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                  form.lineType === 'personal'
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-700 text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                Personal
              </button>
            </div>
          </div>

          {form.lineType === 'commercial' ? (
            <>
              {/* Company Name field */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm(prev => ({ ...prev, company: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="Enter company name"
                  required
                />
              </div>

              {/* Contact Name field */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="Enter contact name"
                  required
                />
              </div>
            </>
          ) : (
            <>
              {/* Primary Contact field */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Primary Contact *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="Enter primary contact name"
                  required
                />
              </div>

              {/* Secondary Contact field */}
              <div>
                <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
                  Secondary Contact
                </label>
                <input
                  type="text"
                  value={form.secondaryContact}
                  onChange={(e) => setForm(prev => ({ ...prev, secondaryContact: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
                  placeholder="Enter secondary contact (e.g., spouse)"
                />
              </div>
            </>
          )}

          {/* Email field */}
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
              placeholder="Enter email address"
            />
          </div>

          {/* Contact Phone field */}
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
              Contact Phone
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
              placeholder="Enter contact phone number"
            />
          </div>

          {/* Status field */}
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-2">
              Status
            </label>
            <select
              value={form.status}
              onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value as 'active' | 'inactive' }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Address section */}
          <div>
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="includeAddress"
                checked={includeAddress}
                onChange={(e) => setIncludeAddress(e.target.checked)}
                className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary dark:focus:ring-primary dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <label htmlFor="includeAddress" className="ml-2 text-sm font-medium text-secondary dark:text-dark-text">
                Add Address (Optional)
              </label>
            </div>

            {includeAddress && (
              <div className="space-y-3 pl-6 border-l-2 border-gray-200 dark:border-gray-600">
                {/* Address Label */}
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                    Address Label
                  </label>
                  <input
                    type="text"
                    value={address.label}
                    onChange={(e) => setAddress(prev => ({ ...prev, label: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                    placeholder="e.g., Headquarters, Main Office"
                  />
                </div>

                {/* Street Address 1 */}
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    value={address.street1}
                    onChange={(e) => setAddress(prev => ({ ...prev, street1: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                    placeholder="Enter street address"
                    required={includeAddress}
                  />
                </div>

                {/* Street Address 2 */}
                <div>
                  <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                    Street Address 2
                  </label>
                  <input
                    type="text"
                    value={address.street2}
                    onChange={(e) => setAddress(prev => ({ ...prev, street2: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                    placeholder="Apt, suite, floor (optional)"
                  />
                </div>

                {/* City, State, ZIP row */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      value={address.city}
                      onChange={(e) => setAddress(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                      placeholder="City"
                      required={includeAddress}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                      State *
                    </label>
                    <input
                      type="text"
                      value={address.state}
                      onChange={(e) => setAddress(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                      placeholder="State"
                      required={includeAddress}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      value={address.postalCode}
                      onChange={(e) => setAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                      placeholder="ZIP"
                      required={includeAddress}
                    />
                  </div>
                </div>

                {/* Country and Address Type row */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                      Country
                    </label>
                    <select
                      value={address.country}
                      onChange={(e) => setAddress(prev => ({ ...prev, country: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="Mexico">Mexico</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary dark:text-dark-text mb-1">
                      Address Type
                    </label>
                    <select
                      value={address.addressType}
                      onChange={(e) => setAddress(prev => ({ ...prev, addressType: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-secondary dark:text-dark-text text-sm"
                    >
                      <option value="primary">Primary</option>
                      <option value="office">Office</option>
                      <option value="billing">Billing</option>
                      <option value="shipping">Shipping</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-secondary dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating || !form.name.trim() || (form.lineType === 'commercial' && !form.company.trim())}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>Create {customerType === 'customer' ? 'Customer' : 'Prospect'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewCustomerModal;
